import { ClickEvent, DataSourceAction, DataSourceExecutionEvent, LookupModelSelectionEvent, TableContentsChangedEvent, TableRow, TableRowCreationEvent, Textbox, Label, LookupModelSearchEvent, BlurEvent } from "@mcleod/components";
import { AutogenLayoutDeductCodeXref } from "./autogen/AutogenLayoutDeductCodeXref";
import { toggleExpandablePanel, updateXrefLabelFormatting, getFilteredRowCount, onBlurCrossReference } from "./DataFusionUtil";

export class DeductCodeXref extends AutogenLayoutDeductCodeXref {
    private _panelDeductExpanded: boolean = false;
    private labelDeductionsCount: Label;
    private _mainLabelTitle: string = "Deductions";

    override async onLoad() {
        await super.onLoad();

        // Create and add the count label next to the main label
        this.createCountLabel();

        // Set initial label formatting and collapse panels by default
        this.updateLabelFormatting(true);
    }

    private createCountLabel() {
        // Create the count label
        this.labelDeductionsCount = new Label({
            text: "",
            color: "primary",
            fontBold: true,
            rowBreak: false,
            marginBottom: 0,
            paddingBottom: 0,
            fontSize: "large"
        });

        // Find the parent container of the main label
        const labelParent = this.labelDeductions.parent;
        if (labelParent && this.horizontalspacer1) {
            // Insert the count label between the main label and the horizontal spacer
            const spacerIndex = labelParent.indexOf(this.horizontalspacer1);
            if (spacerIndex >= 0) {
                labelParent.insert(this.labelDeductionsCount, spacerIndex);
            } else {
                // Fallback: add after the main label
                const labelIndex = labelParent.indexOf(this.labelDeductions);
                if (labelIndex >= 0) {
                    labelParent.insert(this.labelDeductionsCount, labelIndex + 1);
                }
            }
        }
    }

    /** This is an event handler for the onClick event of buttonDeductExpand. */
    async buttonDeductExpandOnClick(event: ClickEvent) {
        this._panelDeductExpanded = await toggleExpandablePanel(
            this._panelDeductExpanded,
            this.panelDeductionCollapse,
            this.buttonDeductionExpand
        );
    }

    /** This is an event handler for the onRowCreate event of tableDeduction. */
    tableDeductionOnRowCreate(event: TableRowCreationEvent) {
        console.log("Row create event triggered");

        if (this.owner?.mainDataSource?.activeRow) {
            const parentRow = this.owner.mainDataSource.activeRow;
            const newRow = event.getTableRow().data;

            // Reset original data FIRST, before setting values, to prevent marking datasource as changed
            newRow.resetOriginalData();

            // Set the required fields from parent
            newRow.set("partner_id", parentRow.get("partner_id"));
            newRow.set("version", parentRow.get("version"));

            // Reset again after setting to clear the dirty flag
            newRow.resetOriginalData();

            // Force the datasource to recalculate its hasChanged state
            this.sourceDeductCodeEdi?.notifyHasChangedComponents(false);
        }

        // Update label formatting and panel state after row creation
        setTimeout(() => {
            this.updateLabelFormatting();
        }, 10);
    }

    private updateLabelFormatting(forceCollapse: boolean = false) {
        const rowCount = getFilteredRowCount(this.tableDeductions);

        // Set the main label text without count
        this.labelDeductions.text = this._mainLabelTitle;

        // Set the count label text and visibility
        if (this.labelDeductionsCount) {
            this.labelDeductionsCount.text = rowCount > 0 ? rowCount.toString() : "";
            this.labelDeductionsCount.visible = rowCount > 0;
        }

        updateXrefLabelFormatting([
            {
                table: this.tableDeductions,
                label: this.labelDeductions,
                isExpanded: this._panelDeductExpanded,
                collapsiblePanel: this.panelDeductionCollapse,
                expandButton: this.buttonDeductionExpand,
                onStateChange: (newState: boolean) => {
                    this._panelDeductExpanded = newState;
                }
            }
        ], forceCollapse);
    }

    /** This is an event handler for the onContentsChanged event of tableDeduction. */
    tableDeductionOnContentsChanged(event: TableContentsChangedEvent) {
        // Update label formatting when table contents change (add/delete rows)
        setTimeout(() => this.updateLabelFormatting(), 10);
    }

    onLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection) {
            const tableRow = TableRow.getContainingTableRow(event.target);
            const textboxDescription = tableRow?.findComponentById("textboxDescription") as Textbox;
            if (textboxDescription) {
                textboxDescription.text = event.selection.get("short_desc");
            }
        }
    }

    /** This is an event handler for the afterExecution event of sourceDeductCode. */
    sourceDeductCodeAfterExecution(event: DataSourceExecutionEvent) {
        // Only apply filtering after search operations and when we have data
        if (event.getAction() === DataSourceAction.SEARCH) {
            // Use setTimeout to ensure the filtering happens after the data is fully loaded
            setTimeout(() => {
                this.updateLabelFormatting(true);
            }, 100);
        }

    }

    public get mainLabelTitle(): string {
        return this._mainLabelTitle;
    }
    public set mainLabelTitle(value: string) {
        this._mainLabelTitle = value;
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxDeductCodeId. */
    textboxDeductCodeIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this._mainLabelTitle === "Earnings") {
            event.filter = { code_type: 'E' };
        }
    }

    async onBlurEdiDeductCode(event: BlurEvent) {
        await onBlurCrossReference(event, this.tableDeductions, "edi_deduct_code");
    }
}
